import {
  useRemoveAssetFromModuleMutation,
  useBulkRestoreAssetsMutation
} from '@ports/internal-services/trash/trashSlice';
import { showToast } from '@business/workflows/ComponentUI/Toast/Toast';
import { useTranslation } from 'react-i18next';

type ModuleType = 'assets' | 'recipes' | 'moments' | 'memories' | 'people';

interface DeleteParams {
  module: ModuleType;
  moduleId: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

interface RestoreParams {
  module: ModuleType;
  moduleId: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

interface DeleteFolderParams {
  module: ModuleType;
  folderId: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

interface RestoreFolderParams {
  module: ModuleType;
  folderId: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export const useTrashRestoreDelete = () => {
  const [RemoveAssetFromModule] = useRemoveAssetFromModuleMutation();
  const [RestoreModule] = useBulkRestoreAssetsMutation();
  const { t } = useTranslation();

  const getDeleteSuccessKey = (module: ModuleType): string => {
    const keys: Record<ModuleType, string> = {
      assets: 'photosPage.assetDeleteSuccess',
      recipes: 'recipesTab.deleteSuccess',
      moments: 'moments.deleteSuccess',
      memories: 'memoriesTab.deleteSuccess',
      people: 'peopleTab.deleteSuccess'
    };
    return keys[module];
  };

  const getDeleteErrorKey = (module: ModuleType): string => {
    const keys: Record<ModuleType, string> = {
      assets: 'photosPage.assetDeleteError',
      recipes: 'recipesTab.deleteError',
      moments: 'moments.deleteError',
      memories: 'memoriesTab.deleteError',
      people: 'peopleTab.deleteError'
    };
    return keys[module];
  };

  const getRestoreSuccessKey = (module: ModuleType): string => {
    const keys: Record<ModuleType, string> = {
      assets: 'assetRecover',
      recipes: 'recipeRecover',
      moments: 'momentRecover',
      memories: 'memoryRecover',
      people: 'personRecover'
    };
    return keys[module];
  };

  const getRestoreErrorKey = (module: ModuleType): string => {
    const keys: Record<ModuleType, string> = {
      assets: 'assetRecoverError',
      recipes: 'recipeRecoverError',
      moments: 'momentRecoverError',
      memories: 'memoryRecoverError',
      people: 'personRecoverError'
    };
    return keys[module];
  };

  const deleteItem = async ({
    module,
    moduleId,
    onSuccess,
    onError
  }: DeleteParams) => {
    try {
      const result = await RemoveAssetFromModule({
        createTrashModule: {
          modules: [{ module, moduleIds: [moduleId] }]
        }
      }).unwrap();

      if (result?.data) {
        showToast({
          type: 'success',
          isAlert: true,
          alertProps: {
            type: 'success',
            message: t(getDeleteSuccessKey(module))
          }
        });
        onSuccess?.();
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: err?.message || t(getDeleteErrorKey(module))
        }
      });
      onError?.(err);
    }
  };

  const restoreItem = async ({
    module,
    moduleId,
    onSuccess,
    onError
  }: RestoreParams) => {
    try {
      const dataObj = {
        bulkRestoreTrash: {
          module,
          moduleId: [moduleId]
        }
      };

      await RestoreModule(dataObj);
      showToast({
        type: 'success',
        isAlert: true,
        alertProps: {
          type: 'success',
          message: t(getRestoreSuccessKey(module))
        }
      });
      onSuccess?.();
    } catch (err: any) {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: err?.message || t(getRestoreErrorKey(module))
        }
      });
      onError?.(err);
    }
  };

  const deleteFolder = async ({
    module,
    folderId,
    onSuccess,
    onError
  }: DeleteFolderParams) => {
    try {
      const result = await RemoveAssetFromModule({
        createTrashModule: {
          modules: [{ module, moduleIds: [], folderIds: [folderId] }]
        }
      }).unwrap();

      if (result?.data) {
        showToast({
          type: 'success',
          isAlert: true,
          alertProps: {
            type: 'success',
            message: t('photosPage.deleteSuccess')
          }
        });
        onSuccess?.();
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: err?.message || t('photosPage.deleteError')
        }
      });
      onError?.(err);
    }
  };

  const restoreFolder = async ({
    module,
    folderId,
    onSuccess,
    onError
  }: RestoreFolderParams) => {
    try {
      const dataObj = {
        bulkRestoreTrash: {
          module,
          folderIds: [folderId]
        }
      };

      await RestoreModule(dataObj);
      showToast({
        type: 'success',
        isAlert: true,
        alertProps: {
          type: 'success',
          message: t('folderRecover')
        }
      });
      onSuccess?.();
    } catch (err: any) {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: err?.message || t('folderRecoverError')
        }
      });
      onError?.(err);
    }
  };

  return {
    deleteItem,
    restoreItem,
    deleteFolder,
    restoreFolder
  };
};
